import { AxiosError } from 'axios'

import { Language } from 'translations/Language'

/** Field types rendered by an action card. */
export type AdminFieldType = 'text' | 'uuid' | 'email' | 'number' | 'select' | 'multiSelect' | 'list' | 'file'

export interface AdminActionField {
  key: string
  label: string
  type: AdminFieldType
  required?: boolean
  placeholder?: string
  /** Options for `select` / `multiSelect` fields. */
  options?: string[]
  /** Initial value for the field. */
  defaultValue?: string
  /** Value for the file input's `accept` attribute. */
  accept?: string
}

export interface AdminActionRequest {
  path: string
  params?: Record<string, string | number>
  data?: object | Blob
  accept?: string
}

export interface AdminAction {
  id: string
  label: string
  description?: string
  method: 'GET' | 'POST' | 'PUT'
  /** Builds the request path / query params / body from the collected field values. */
  buildRequest: (values: Record<string, string>, files: Record<string, File>) => AdminActionRequest
  fields?: AdminActionField[]
  /** When set, the user must confirm with this message before the action fires. */
  confirm?: string
  /** Known slow endpoints: a gateway timeout is treated as "dispatched", not an error. */
  longRunning?: boolean
  /** The response is a file: saved through the browser instead of rendered. */
  download?: boolean
}

export interface AdminSection {
  id: string
  title: string
  description?: string
  actions: AdminAction[]
}

/** A successful action call, normalized for rendering. */
export interface ActionResult {
  status: number
  data: unknown
}

export type ActionRunner = (
  action: AdminAction,
  values: Record<string, string>,
  files: Record<string, File>,
) => Promise<ActionResult>

/**
 * A long-running action whose socket the gateway severed (GCP LB timeout ~30s) is treated as
 * dispatched, not failed — the backend keeps running and the operations here are idempotent.
 */
const DISPATCH_STATUSES = [502, 503, 504]

export const isDispatchedTimeout = (error: AxiosError): boolean =>
  error.code === 'ECONNABORTED' || !error.response || DISPATCH_STATUSES.includes(error.response.status)

/** Mirrors the backend `AutoenhanceVisualStatus` enum. */
const AUTOENHANCE_STATUSES = ['PROCESSING', 'PREVIEW_READY', 'FULL_RES_DOWNLOADING', 'FULL_RES_READY', 'ERROR']

/** Splits a comma / newline / whitespace separated string into trimmed, non-empty tokens. */
const splitTokens = (raw: string): string[] => raw.split(/[\s,]+/).map(token => token.trim()).filter(Boolean)

export const ADMIN_SECTIONS: AdminSection[] = [
  {
    id: 'system',
    title: 'System & Catalogue',
    description: 'Global, idempotent re-syncs and catalogue refreshes.',
    actions: [
      {
        id: 'product2-sync-one',
        label: 'Sync single Product2 from Salesforce',
        description: 'Re-sync one Product2 record by its Salesforce Id.',
        method: 'POST',
        fields: [{ key: 'sfProductId', label: 'Salesforce product Id', type: 'text', required: true, placeholder: '15–18 char Salesforce Id' }],
        buildRequest: ({ sfProductId }) => ({ path: `/product2/sf-product/${sfProductId}/sync` }),
      },
      {
        id: 'product2-sync-all',
        label: 'Sync ALL Product2 from Salesforce',
        description: 'Full catalogue re-sync. Runs for several minutes server-side.',
        method: 'POST',
        longRunning: true,
        confirm: 'Sync ALL Product2 products from Salesforce? This can take several minutes.',
        buildRequest: () => ({ path: '/product2/sf-product/all/sync' }),
      },
      {
        id: 'product-refresh',
        label: 'Refresh products',
        description: 'Refresh the legacy product cache, optionally scoped to a country.',
        method: 'GET',
        longRunning: true,
        fields: [{ key: 'countryCode', label: 'Country code (optional)', type: 'text', placeholder: 'e.g. DE' }],
        buildRequest: ({ countryCode }) => ({ path: '/product/refresh', params: countryCode ? { countryCode } : undefined }),
      },
      {
        id: 'product-all',
        label: 'List all products',
        method: 'GET',
        buildRequest: () => ({ path: '/product/all' }),
      },
      {
        id: 'editor-products',
        label: 'List eWarp editor products',
        method: 'GET',
        buildRequest: () => ({ path: '/editor/products' }),
      },
      {
        id: 'virtual-furniture-refresh',
        label: 'Refresh virtual furniture templates',
        method: 'POST',
        buildRequest: () => ({ path: '/virtualStaging/virtualFurniture/refresh' }),
      },
      {
        id: 'translation-sync',
        label: 'Sync translations',
        description: 'Re-pull translations from source. Optionally scope to one language.',
        method: 'POST',
        longRunning: true,
        confirm: 'Re-sync all translations from source?',
        fields: [{ key: 'language', label: 'Language (optional)', type: 'select', options: ['', ...Object.values(Language)] }],
        buildRequest: ({ language }) => ({ path: '/translation/all/sync', params: language ? { language } : undefined }),
      },
      {
        id: 'workspace-sync',
        label: 'Sync all workspace users',
        method: 'POST',
        longRunning: true,
        confirm: 'Sync ALL workspace users? This can take a while.',
        fields: [{ key: 'chunkSize', label: 'Chunk size', type: 'number', defaultValue: '10' }],
        buildRequest: ({ chunkSize }) => ({ path: '/workspace/sync', params: chunkSize ? { chunkSize: Number(chunkSize) } : undefined }),
      },
      {
        id: 'refresh-guidelines-cache',
        label: 'Refresh guidelines cache',
        method: 'POST',
        confirm: 'Refresh the guidelines cache now?',
        buildRequest: () => ({ path: '/admin/refresh-guidelines-cache' }),
      },
    ],
  },
  {
    id: 'resync',
    title: 'Resync by ID',
    description: 'Idempotent re-push of a single entity to the CRM.',
    actions: [
      {
        id: 'order-sync',
        label: 'Resync order to CRM',
        method: 'POST',
        fields: [{ key: 'orderPublicId', label: 'Order public id', type: 'number', required: true }],
        buildRequest: ({ orderPublicId }) => ({ path: `/order/${orderPublicId}/sync` }),
      },
      {
        id: 'assignment-sync',
        label: 'Resync assignment to CRM',
        method: 'POST',
        fields: [{ key: 'assignmentId', label: 'Assignment id', type: 'text', required: true }],
        buildRequest: ({ assignmentId }) => ({ path: `/assignment/${assignmentId}/sync` }),
      },
      {
        id: 'resume-crm-update',
        label: 'Resume CRM update',
        description: 'Re-publishes the unfinished CRM updates of a transaction.',
        method: 'POST',
        fields: [{ key: 'txId', label: 'Transaction id', type: 'uuid', required: true }],
        buildRequest: ({ txId }) => ({ path: `/admin/resume-crm-update/${txId}` }),
      },
    ],
  },
  {
    id: 'jobs',
    title: 'Job Replay & Reprocessing',
    description: 'Recovery actions for stuck jobs and AutoEnhance / Esoft assets.',
    actions: [
      {
        id: 'job-status',
        label: 'Job status',
        method: 'GET',
        fields: [{ key: 'id', label: 'Job id', type: 'uuid', required: true }],
        buildRequest: ({ id }) => ({ path: `/job/status/${id}` }),
      },
      {
        id: 'replay-stuck-jobs',
        label: 'Replay stuck jobs',
        description: 'Replays the listed jobs. Comma / newline separated job ids.',
        method: 'POST',
        fields: [{ key: 'jobIds', label: 'Job ids', type: 'list', required: true, placeholder: 'UUIDs, comma or newline separated' }],
        buildRequest: ({ jobIds }) => ({ path: '/admin/replay-stuck-jobs', data: splitTokens(jobIds) as unknown as object }),
      },
      {
        id: 'reprocess-stuck-autoenhance',
        label: 'Reprocess stuck AutoEnhance files',
        method: 'POST',
        fields: [{ key: 'statuses', label: 'Statuses', type: 'multiSelect', required: true, options: AUTOENHANCE_STATUSES }],
        buildRequest: ({ statuses }) => ({ path: '/admin/reprocess-stuck-autoenhance-files', params: { statuses } }),
      },
      {
        id: 'reprocess-autoenhance-file',
        label: 'Reprocess one AutoEnhance file',
        method: 'POST',
        fields: [{ key: 'autoenhanceFileId', label: 'AutoEnhance file id', type: 'uuid', required: true }],
        buildRequest: ({ autoenhanceFileId }) => ({ path: `/admin/reprocess-autoenhance-file/${autoenhanceFileId}` }),
      },
      {
        id: 'retrigger-autoenhance-upload',
        label: 'Retrigger AutoEnhance upload',
        method: 'POST',
        fields: [{ key: 'assignmentPublicId', label: 'Assignment public id', type: 'text', required: true }],
        buildRequest: ({ assignmentPublicId }) => ({ path: `/admin/retrigger-autoenhance-upload/${assignmentPublicId}` }),
      },
      {
        id: 'retrigger-esoft-download',
        label: 'Retrigger Esoft asset download',
        method: 'POST',
        fields: [{ key: 'assignmentPublicId', label: 'Assignment public id', type: 'text', required: true }],
        buildRequest: ({ assignmentPublicId }) => ({ path: `/admin/retrigger-esoft-asset-download/${assignmentPublicId}` }),
      },
      {
        id: 'rerun-docestate-events',
        label: 'Re-run DocEstate events',
        method: 'POST',
        fields: [{ key: 'amount', label: 'Amount', type: 'number', defaultValue: '5' }],
        buildRequest: ({ amount }) => ({ path: '/admin/re-run-docestate-events', params: amount ? { amount: Number(amount) } : undefined }),
      },
      {
        id: 'resend-docestate-order',
        label: 'Re-send DocEstate order',
        method: 'POST',
        fields: [
          { key: 'orderPublicId', label: 'Order public id', type: 'number', required: true },
          { key: 'parentJobId', label: 'Parent job id', type: 'uuid', required: true },
        ],
        buildRequest: ({ orderPublicId, parentJobId }) => ({
          path: '/admin/re-send-docestate-order',
          params: { orderPublicId: Number(orderPublicId), parentJobId },
        }),
      },
    ],
  },
  {
    id: 'lookups',
    title: 'User & Workspace lookups',
    description: 'Read-only diagnostics plus the locale override.',
    actions: [
      {
        id: 'user-status',
        label: 'User status by email',
        method: 'GET',
        fields: [{ key: 'email', label: 'Client email', type: 'email', required: true }],
        buildRequest: ({ email }) => ({ path: '/user/status', params: { email } }),
      },
      {
        id: 'workspace-subscription',
        label: 'Workspace subscription status',
        method: 'GET',
        fields: [{ key: 'workspaceId', label: 'Workspace id', type: 'uuid', required: true }],
        buildRequest: ({ workspaceId }) => ({ path: `/workspace/${workspaceId}/subscription` }),
      },
      {
        id: 'user-locale',
        label: 'Set user locale',
        method: 'PUT',
        fields: [
          { key: 'email', label: 'Client email', type: 'email', required: true },
          { key: 'language', label: 'Language', type: 'select', required: true, options: Object.values(Language) },
        ],
        buildRequest: ({ email, language }) => ({ path: '/user/locale', params: { email }, data: { language } }),
      },
    ],
  },
  {
    id: 'property-import',
    title: 'Property import',
    description:
      'Uploading starts the dry run; follow it with Import status. The dry run writes nothing to properties. Apply needs the CREATE_UNIT count from the status card\'s decisionCounts (0 when absent).',
    actions: [
      {
        id: 'property-import-upload',
        label: 'Upload property CSV',
        method: 'POST',
        fields: [
          { key: 'workspaceId', label: 'Workspace id', type: 'uuid', required: true },
          { key: 'file', label: 'CSV file', type: 'file', required: true, accept: '.csv,text/csv' },
        ],
        buildRequest: ({ workspaceId }, files) => {
          const data = new FormData()
          data.append('file', files.file)
          return { path: '/admin/property-imports', params: { workspaceId }, data }
        },
      },
      {
        id: 'property-import-list',
        label: 'List imports',
        method: 'GET',
        fields: [{ key: 'workspaceId', label: 'Workspace id', type: 'uuid', required: true }],
        buildRequest: ({ workspaceId }) => ({ path: '/admin/property-imports', params: { workspaceId } }),
      },
      {
        id: 'property-import-status',
        label: 'Import status',
        method: 'GET',
        fields: [{ key: 'importId', label: 'Import id', type: 'uuid', required: true }],
        buildRequest: ({ importId }) => ({ path: `/admin/property-imports/${importId}` }),
      },
      {
        id: 'property-import-rows',
        label: 'Download rows CSV',
        method: 'GET',
        download: true,
        fields: [{ key: 'importId', label: 'Import id', type: 'uuid', required: true }],
        buildRequest: ({ importId }) => ({ path: `/admin/property-imports/${importId}/rows.csv`, accept: 'text/csv, application/json' }),
      },
      {
        id: 'property-import-dry-run',
        label: 'Re-run dry run',
        description: 'Recomputes the dry run of a finished, failed or stalled import in the background (202). Check Import status for the result.',
        method: 'POST',
        confirm: 'Recompute all decisions for this import?',
        fields: [{ key: 'importId', label: 'Import id', type: 'uuid', required: true }],
        buildRequest: ({ importId }) => ({ path: `/admin/property-imports/${importId}/dry-run` }),
      },
      {
        id: 'property-import-apply',
        label: 'Apply import',
        description: 'Starts the apply in the background (202). Check Import status for the final status, applyCounts.failed and failureReason.',
        method: 'POST',
        confirm: 'Apply writes properties in this environment. Continue?',
        fields: [
          { key: 'importId', label: 'Import id', type: 'uuid', required: true },
          { key: 'expectedCreates', label: 'Expected creates', type: 'number', required: true },
        ],
        buildRequest: ({ importId, expectedCreates }) => ({
          path: `/admin/property-imports/${importId}/apply`,
          params: { expectedCreates: Number(expectedCreates) },
        }),
      },
    ],
  },
]
